import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ConfigStateService, LocalizationPipe } from '@abp/ng.core';
import { Observable, merge, switchMap } from 'rxjs';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { BaseFormComponent } from 'src/core/forms/base-form.component';
import { FORM_SHARED_IMPORTS } from 'src/app/shared/imports/form-shared.imports';
import { AnsweringMachineDetectionSettingsDto } from 'src/core/models/settings/answering-machine-detection-settings/answering-machine-detection-settings.dto';
import { CustomSettingsService } from 'src/app/proxy/custom-settings.service';
import type { SettingsDto } from 'src/app/proxy/dtos/settings/models';

/** Setting names as registered by the backend's `AnsweringMachineDetectionSettingsProvider`. */
const ENABLED_SETTING = 'AnsweringMachineDetection.Enabled';
const AGENT_OUTBOUND_ENABLED_SETTING = 'AnsweringMachineDetection.AgentOutboundEnabled';
const DETECTION_TIMEOUT_SETTING = 'AnsweringMachineDetection.DetectionTimeoutSeconds';

/** Backend clamps the stored value to this range and falls back to the default for anything unparsable. */
const MIN_TIMEOUT_SECONDS = 5;
const MAX_TIMEOUT_SECONDS = 120;
const DEFAULT_TIMEOUT_SECONDS = 30;

/**
 * Integration &gt; Answering Machine Detection tab: opts the current tenant into hanging up automatically
 * when an outbound call is answered by a voicemail beep, separately for server-originated (AI) calls and
 * for agent outbound dials, and holds how long detection listens after the call is answered.
 *
 * <p>Tenant-scoped by construction - the whole Integration screen is removed for the ABP host user
 * (see `AppComponent`), and the backend restricts all three settings to the tenant value provider.</p>
 */
@Component({
  selector: 'app-answering-machine-detection-settings',
  standalone: true,
  templateUrl: './answering-machine-detection-settings.component.html',
  styleUrls: ['../integration-settings-form.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [...FORM_SHARED_IMPORTS, LocalizationPipe, ToggleSwitchModule],
})
export class AnsweringMachineDetectionSettingsComponent extends BaseFormComponent<AnsweringMachineDetectionSettingsDto> {
  readonly minTimeoutSeconds = MIN_TIMEOUT_SECONDS;
  readonly maxTimeoutSeconds = MAX_TIMEOUT_SECONDS;

  private readonly customSettingsService = inject(CustomSettingsService);
  private readonly configStateService = inject(ConfigStateService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    super(inject(ChangeDetectorRef));
  }

  protected buildForm(): FormGroup {
    return new FormGroup({
      enabled: new FormControl<boolean>(false, { nonNullable: true }),
      agentOutboundEnabled: new FormControl<boolean>(false, { nonNullable: true }),
      detectionTimeoutSeconds: new FormControl<number | null>(DEFAULT_TIMEOUT_SECONDS, {
        validators: [Validators.min(MIN_TIMEOUT_SECONDS), Validators.max(MAX_TIMEOUT_SECONDS)],
      }),
    });
  }

  protected override afterFormInit(): void {
    this.loadSettings();
    this.applyTimeoutRequirement();

    merge(
      this.form.controls['enabled'].valueChanges,
      this.form.controls['agentOutboundEnabled'].valueChanges
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.applyTimeoutRequirement();
        this.cdr.markForCheck();
      });
  }

  protected submitRequest(value: AnsweringMachineDetectionSettingsDto): Observable<unknown> {
    const settings: SettingsDto[] = [
      { settingName: ENABLED_SETTING, settingValue: String(value.enabled) },
      {
        settingName: AGENT_OUTBOUND_ENABLED_SETTING,
        settingValue: String(value.agentOutboundEnabled),
      },
      {
        // A fully opted-out tenant may have cleared the field; keep the stored value parsable.
        settingName: DETECTION_TIMEOUT_SETTING,
        settingValue: String(value.detectionTimeoutSeconds ?? DEFAULT_TIMEOUT_SECONDS),
      },
    ];

    return this.customSettingsService
      .set(settings)
      .pipe(switchMap(() => this.configStateService.refreshAppState()));
  }

  private loadSettings(): void {
    const timeout = Number.parseInt(
      this.configStateService.getSetting(DETECTION_TIMEOUT_SETTING) ?? '',
      10
    );

    this.setValue({
      enabled: this.readFlag(ENABLED_SETTING),
      agentOutboundEnabled: this.readFlag(AGENT_OUTBOUND_ENABLED_SETTING),
      detectionTimeoutSeconds: Number.isNaN(timeout) ? DEFAULT_TIMEOUT_SECONDS : timeout,
    });
  }

  private readFlag(settingName: string): boolean {
    return this.configStateService.getSetting(settingName)?.toLowerCase() === 'true';
  }

  /**
   * The timeout is only mandatory while detection is armed for at least one call type; a fully opted-out
   * tenant may leave it blank without the form blocking the save.
   */
  private applyTimeoutRequirement(): void {
    const armed =
      (this.form.controls['enabled'].value as boolean) ||
      (this.form.controls['agentOutboundEnabled'].value as boolean);
    const timeout = this.form.controls['detectionTimeoutSeconds'];
    const range = [Validators.min(MIN_TIMEOUT_SECONDS), Validators.max(MAX_TIMEOUT_SECONDS)];

    timeout.setValidators(armed ? [Validators.required, ...range] : range);
    timeout.updateValueAndValidity({ emitEvent: false });
  }
}
